"use client"

import { Link } from "react-router-dom"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import { daysSince } from "@/features/dashboard/home-d4/lib/home-format"
import { WorkQueueItemType, type WorkQueueSummary } from "@/features/work-queue/model/work-queue.types"
import { useTranslations } from "@/i18n"
import { Button, cn } from "@ibada/ui"

/** Display order and tint of each pending-work type. */
const TYPE_ORDER: readonly WorkQueueItemType[] = [
  WorkQueueItemType.MemberConfirmation,
  WorkQueueItemType.BudgetReview,
  WorkQueueItemType.TransferApproval,
  WorkQueueItemType.ReconciliationReview,
]

const TYPE_TINT: Record<WorkQueueItemType, string> = {
  [WorkQueueItemType.MemberConfirmation]: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  [WorkQueueItemType.BudgetReview]: "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  [WorkQueueItemType.TransferApproval]: "bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  [WorkQueueItemType.ReconciliationReview]: "bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
}

/** The groups worth a row: real pending items only, in display order. */
export function pendingApprovalGroups(summary: WorkQueueSummary) {
  return TYPE_ORDER.map((type) => summary.groups.find((group) => group.type === type)).filter(
    (group): group is NonNullable<typeof group> => Boolean(group && group.count > 0),
  )
}

/**
 * "To validate" (D4): what is waiting on this reader in this branch, one row per kind of work with how many and
 * since when the oldest has waited. "Process all" opens the work queue, which owns the actual decisions.
 */
export function ApprovalsTile({ summary, workQueueHref, now }: { summary: WorkQueueSummary; workQueueHref: string; now: Date }) {
  const t = useTranslations("dashboard.home.approvals")
  const groups = pendingApprovalGroups(summary)
  const total = groups.reduce((sum, group) => sum + group.count, 0)

  return (
    <HomeTile
      title={t("title")}
      subtitle={total === 0 ? null : t(total === 1 ? "subtitleOne" : "subtitleOther", { count: total })}
      testId='home-tile-approvals'
    >
      {groups.length === 0 ? <HomeTileEmpty message={t("empty")} /> : null}
      <ul className='flex flex-col divide-y divide-border/60'>
        {groups.map((group) => {
          const days = daysSince(group.oldestSubmittedAt, now)
          return (
            <li key={group.type} data-testid={`approval-row-${group.type}`} className='flex items-center gap-2.5 py-2 first:pt-0'>
              <span
                className={cn(
                  "inline-flex h-[26px] min-w-[30px] items-center justify-center rounded-lg px-1.5 text-[13px] font-extrabold tabular-nums",
                  TYPE_TINT[group.type],
                )}
              >
                {group.count}
              </span>
              <span className='min-w-0 truncate text-[13px] font-semibold text-foreground'>{t(`types.${group.type}` as never)}</span>
              {days !== null ? (
                <span className='ml-auto shrink-0 text-[11.5px] text-muted-foreground'>{days === 0 ? t("waitingToday") : t(days === 1 ? "waitingOne" : "waitingOther", { count: days })}</span>
              ) : null}
            </li>
          )
        })}
      </ul>
      {groups.length > 0 ? (
        <Button asChild size='sm' className='mt-auto w-full font-bold'>
          <Link to={workQueueHref}>{t("processAll")}</Link>
        </Button>
      ) : null}
    </HomeTile>
  )
}
